/** @jsxImportSource @emotion/react */
import React, { ChangeEvent, useMemo, useState } from "react";
import { Button, Card, Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField";
import DateField from "@components/FormsV2/DateField";
import Select2V2 from "@components/FormsV2/Select2";
import { ReleaseFlagFormValues, STATUS_OPTIONS } from "../types";
import { normalizeKey, keyValidationError } from "./keyHelpers";
import useStyles from "../styles/useStyles";

interface Props {
  mode: "new" | "edit";
  title: string;
  initialValues: ReleaseFlagFormValues;
  onSubmit: (values: ReleaseFlagFormValues) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const ReleaseFlagForm: React.FC<Props> = ({
  mode,
  title,
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const styles = useStyles();
  const [values, setValues] = useState<ReleaseFlagFormValues>(initialValues);

  const keyError = mode === "new" ? keyValidationError(values.key) : null;
  const normalizedKeyPreview = useMemo(
    () => (values.key ? normalizeKey(values.key) : ""),
    [values.key]
  );

  const requiredMissing =
    !values.name.trim() ||
    !values.betaReleaseDate ||
    !values.generalAvailabilityDate ||
    (mode === "new" && keyError !== null);

  const handleSave = () => {
    if (requiredMissing) return;
    onSubmit({
      ...values,
      key: mode === "new" ? normalizeKey(values.key) : values.key,
    });
  };

  const statusOption =
    STATUS_OPTIONS.find((o) => o.value === values.status) || STATUS_OPTIONS[0];

  return (
    <Card>
      <Card.Header className="headerOverride">
        <div className="headerContainer">
          <div className="actionBtns">
            <Button className="btnSeawhite marginRight10" onClick={onCancel}>
              Cancel
            </Button>
            <h2 className="headerText">{title}</h2>
          </div>
          <div className="actionBtns">
            <Button
              className="squareGreenButton"
              onClick={handleSave}
              disabled={isSubmitting || requiredMissing}
            >
              Save
            </Button>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="release-flag-name">Name</Form.Label>
            <TextField
              label={null}
              id="release-flag-name"
              value={values.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setValues({ ...values, name: e.target.value })
              }
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            {mode === "new" ? (
              <>
                <Form.Label htmlFor="release-flag-key">Key</Form.Label>
                <TextField
                  label={null}
                  id="release-flag-key"
                  value={values.key}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setValues({ ...values, key: e.target.value })
                  }
                  required
                />
                {normalizedKeyPreview && (
                  <div css={styles.keyPreview}>
                    Will be saved as: {normalizedKeyPreview}
                  </div>
                )}
                {keyError && values.key && (
                  <Form.Text className="text-danger">{keyError}</Form.Text>
                )}
              </>
            ) : (
              <>
                <Form.Label>Key</Form.Label>
                <div css={styles.monospace}>{values.key}</div>
                <div css={styles.helperText}>
                  Key cannot be changed after creation.
                </div>
              </>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="release-flag-description">
              Description
            </Form.Label>
            <TextField
              label={null}
              id="release-flag-description"
              value={values.description}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setValues({ ...values, description: e.target.value })
              }
              placeholder="Shown to end users when they opt in or out of this feature. Make it clear what enabling it changes."
            />
          </Form.Group>

          <Select2V2
            label="Status"
            value={statusOption}
            options={STATUS_OPTIONS}
            onChange={(opt: { value: (typeof values)["status"] }) =>
              setValues({ ...values, status: opt.value })
            }
          />

          <Form.Group className="mb-3">
            <DateField
              label="Beta Release Date"
              id="release-flag-beta-date"
              value={values.betaReleaseDate}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setValues({ ...values, betaReleaseDate: e.target.value })
              }
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <DateField
              label="General Availability Date"
              id="release-flag-ga-date"
              value={values.generalAvailabilityDate}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setValues({
                  ...values,
                  generalAvailabilityDate: e.target.value,
                })
              }
              required
            />
          </Form.Group>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default ReleaseFlagForm;
